import { onlineManager } from '@tanstack/react-query';

import { isNativePlatform } from './platform';

let nativeStatusPrime: Promise<boolean | null> | null = null;
let nativeBridgeOwner: symbol | null = null;

function ignorePromiseRejection(promise: Promise<unknown>): void {
  promise.catch(() => null);
}

function removeNetworkListener(listener: { remove: () => Promise<void> }): void {
  ignorePromiseRejection(listener.remove());
}

/**
 * Resolve native connectivity before auth or tenant queries make their first
 * routing decision. This is a single-flight probe only while the read is in
 * progress: settled and failed reads must be retryable because native network
 * state can change while the process remains alive.
 */
export async function primeNativeNetworkStatus(): Promise<boolean | null> {
  if (!isNativePlatform()) {
    return null;
  }

  nativeStatusPrime ??= (async () => {
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();

      // Once the bridge starts, its listener-first reconciliation is the
      // sole state owner. A slower bootstrap probe must not overwrite that
      // fresher result after the listener has taken ownership.
      if (!nativeBridgeOwner) {
        onlineManager.setOnline(status.connected);
      }

      return status.connected;
    } catch {
      return null;
    } finally {
      nativeStatusPrime = null;
    }
  })();

  return await nativeStatusPrime;
}

export function __resetNativeNetworkStatusForTests(): void {
  nativeStatusPrime = null;
  nativeBridgeOwner = null;
}

/**
 * Drives TanStack Query's `onlineManager` from the Capacitor Network plugin.
 *
 * Without this, native has no trustworthy offline signal at all: WKWebView's
 * `online`/`offline` window events - what `onlineManager` listens to by
 * default - are unreliable inside the shell, so queries either pause against
 * a network that works or fire against one that does not. Every future
 * offline-capable query family builds on this bridge; it is the foundation
 * the encrypted offline cache plugs into.
 *
 * `onReconnect` fires only on an offline -> online EDGE. That includes a fresh
 * listener-owned reconciliation following an earlier offline bootstrap probe:
 * the resume pipeline must settle before TanStack publishes online, otherwise
 * paused queries can resume against a stale JWT.
 *
 * Web is untouched: the browser events are fine there, and this module
 * no-ops on anything that is not the native shell.
 */
export async function initNativeNetworkBridge(
  onReconnect?: () => Promise<void>,
): Promise<(() => void) | null> {
  if (!isNativePlatform()) {
    return null;
  }

  const owner = Symbol('native-network-bridge');
  nativeBridgeOwner = owner;

  let Network: typeof import('@capacitor/network').Network;
  try {
    ({ Network } = await import('@capacitor/network'));
  } catch (error) {
    if (nativeBridgeOwner === owner) {
      nativeBridgeOwner = null;
    }
    throw error;
  }

  let wasConnected = onlineManager.isOnline();
  let activeSetupCleanup: (() => void) | null = null;
  let transitionTail: Promise<unknown> = Promise.resolve();
  let disposed = false;

  let resolveInitialSetup: (() => void) | null = null;
  const initialSetup = new Promise<void>((resolve) => {
    resolveInitialSetup = resolve;
  });

  const settleInitialSetup = () => {
    resolveInitialSetup?.();
    resolveInitialSetup = null;
  };

  const enqueueStatus = async (
    connected: boolean,
    setOnline: (online: boolean) => void,
    setupIsCurrent: () => boolean,
  ): Promise<void> => {
    const transition = transitionTail.then(async () => {
      if (disposed || !setupIsCurrent()) {
        return;
      }

      if (connected && !wasConnected) {
        try {
          await onReconnect?.();
        } finally {
          // Connectivity remains a physical fact even if a caller rejects.
          // Publish it only after the reconnect attempt has settled.
          if (!disposed && setupIsCurrent()) {
            setOnline(true);
            wasConnected = true;
          }
        }
        return;
      }

      setOnline(connected);
      wasConnected = connected;
    });

    // A rejected reconnect callback must not poison later network events.
    transitionTail = transition.catch(() => null);
    await transition;
  };

  // setEventListener REPLACES TanStack's default window online/offline
  // subscription - which matters as much as the plugin wiring itself: those
  // window events are the unreliable signal this bridge exists to escape,
  // and left subscribed they would keep flipping onlineManager underneath
  // the plugin, including manufacturing false reconnect edges.
  onlineManager.setEventListener((setOnline) => {
    if (disposed) {
      settleInitialSetup();
      return settleInitialSetup;
    }

    let setupDisposed = false;
    let eventVersion = 0;
    let removePluginListener: (() => void) | null = null;

    const cleanup = () => {
      setupDisposed = true;
      removePluginListener?.();
      removePluginListener = null;
      if (activeSetupCleanup === cleanup) {
        activeSetupCleanup = null;
      }
    };
    activeSetupCleanup = cleanup;

    const setupIsCurrent = () => !setupDisposed && activeSetupCleanup === cleanup;
    const handleStatusChange = (status: { connected: boolean }): void => {
      eventVersion += 1;
      ignorePromiseRejection(enqueueStatus(status.connected, setOnline, setupIsCurrent));
    };

    const registerAndReconcile = async (): Promise<void> => {
      try {
        const listener = await Network.addListener('networkStatusChange', handleStatusChange);
        const remove = () => {
          removeNetworkListener(listener);
        };

        if (disposed || !setupIsCurrent()) {
          remove();
          return;
        }
        removePluginListener = remove;

        // The listener owns the edge before this read begins. If an event
        // arrives while getStatus is pending, that event is newer and the
        // reconciliation snapshot is discarded rather than overwriting it.
        const versionBeforeRead = eventVersion;
        try {
          const status = await Network.getStatus();
          if (versionBeforeRead === eventVersion && setupIsCurrent()) {
            await enqueueStatus(status.connected, setOnline, setupIsCurrent);
          }
        } catch {
          // Plugin unavailable: retain the last trustworthy state. The live
          // listener can still reconcile the next status edge.
        }
      } finally {
        settleInitialSetup();
      }
    };

    registerAndReconcile().catch(() => null);
    return cleanup;
  });

  await initialSetup;

  return () => {
    disposed = true;
    activeSetupCleanup?.();
    activeSetupCleanup = null;
    if (nativeBridgeOwner === owner) {
      nativeBridgeOwner = null;
    }
  };
}
